// Flow graphs: the automation a site builds on the Flows canvas.
//
// A graph is a list of nodes; each node names the node that runs after it
// (`next`, or one target per branch). There is no separate edge list, so a
// graph can't hold an edge to nowhere that the canvas would hide. The canvas
// derives its edges from these fields and keeps each node's `position`.
//
// The same graph runs in two places: the server's flow engine and the agent
// app's test runner, both through interpret.ts. Nothing here touches the
// database or the network.

export const FLOW_GRAPH_VERSION = 1;

/** A variable's value. Contact attributes and question answers are strings;
 *  REST responses may add numbers and booleans. */
export type FlowValue = string | number | boolean | null;
export type FlowVars = Record<string, FlowValue>;

export interface FlowPosition {
  x: number;
  y: number;
}

/** What starts a run. One per graph, on the trigger node. */
export type FlowTrigger =
  /** A visitor opens a new conversation. */
  | { kind: 'conversation_started' }
  /** A visitor message arrives with no run active on the conversation.
   *  `keywords` (any, case-insensitive substring) narrows it; empty = any. */
  | { kind: 'message_received'; keywords?: string[] }
  /** Only started by hand: the test runner, or another system via the API. */
  | { kind: 'manual' };

export type ConditionOp =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'not_contains'
  | 'exists'
  | 'not_exists'
  | 'gt'
  | 'lt'
  | 'in';

export interface FlowCondition {
  /** Variable name, e.g. `answer` or `contact.email`. */
  var: string;
  op: ConditionOp;
  /** Unused for exists/not_exists; a list for `in`. */
  value?: string | number | string[];
}

interface NodeBase {
  id: string;
  position?: FlowPosition;
  /** Shown on the canvas instead of the type name. */
  label?: string;
}

export interface TriggerNode extends NodeBase {
  type: 'trigger';
  trigger: FlowTrigger;
  next: string | null;
}

export interface MessageNode extends NodeBase {
  type: 'message';
  /** Template: `{{var}}` is replaced by that variable's value. */
  text: string;
  next: string | null;
}

export interface QuestionNode extends NodeBase {
  type: 'question';
  text: string;
  /** Where the visitor's reply is stored. */
  variable: string;
  /** Quick replies offered with the question. The reply is still free text. */
  options?: string[];
  /** Give up waiting after this long and go to `onTimeout` (or end). */
  timeoutMs?: number;
  next: string | null;
  onTimeout?: string | null;
}

export interface ConditionNode extends NodeBase {
  type: 'condition';
  /** First rule that matches wins; all conditions in a rule must hold. */
  rules: Array<{ when: FlowCondition[]; next: string | null }>;
  else: string | null;
}

export interface DelayNode extends NodeBase {
  type: 'delay';
  ms: number;
  next: string | null;
}

export interface TagNode extends NodeBase {
  type: 'tag';
  add?: string[];
  remove?: string[];
  next: string | null;
}

/** Save a value on the visitor's contact (customer attributes). The value is
 *  a template, so `{{answer}}` stores what the visitor just replied; later
 *  nodes and later conversations read it back as `contact.<key>`. */
export interface SetAttributeNode extends NodeBase {
  type: 'set_attribute';
  /** Lower-case letters, digits and `_`: `plan`, `order_id`. */
  key: string;
  value: string;
  next: string | null;
}

export type AssignTarget = { kind: 'agent'; agentId: string } | { kind: 'unassigned' };

export interface AssignNode extends NodeBase {
  type: 'assign';
  to: AssignTarget;
  next: string | null;
}

/** Ends the run and closes the conversation. */
export interface CloseNode extends NodeBase {
  type: 'close';
}

/** Fire-and-forget POST of the run's variables; the flow doesn't wait. */
export interface WebhookNode extends NodeBase {
  type: 'webhook';
  url: string;
  next: string | null;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Calls an API, waits for the answer, and can save parts of it. */
export interface RestNode extends NodeBase {
  type: 'rest';
  method: HttpMethod;
  /** Template. */
  url: string;
  /** Header values are templates. */
  headers?: Record<string, string>;
  /** Template; sent as-is (JSON is the caller's to write). */
  body?: string;
  /** Variable name → dotted path into the JSON response (`order.status`).
   *  `$status` saves the HTTP status code instead. */
  save?: Record<string, string>;
  next: string | null;
  /** Taken on a network error or a non-2xx status; absent = the run fails. */
  onError?: string | null;
}

export type FlowNode =
  | TriggerNode
  | MessageNode
  | QuestionNode
  | ConditionNode
  | DelayNode
  | TagNode
  | SetAttributeNode
  | AssignNode
  | CloseNode
  | WebhookNode
  | RestNode;

export type FlowNodeType = FlowNode['type'];

export interface FlowGraph {
  version: typeof FLOW_GRAPH_VERSION;
  nodes: FlowNode[];
}

// ─── Runs ───

export type FlowRunStatus = 'running' | 'waiting' | 'completed' | 'failed';

/** What a waiting run is waiting for. `token` must come back with the input
 *  that resumes it, so a stale or repeated input (a timer job delivered
 *  twice, a timeout that fires after the reply) is ignored. */
export type FlowWait =
  | { kind: 'reply'; nodeId: string; token: number }
  /** `ms` is the delay as configured; the caller's timer decides when it
   *  ends (the interpreter has no clock). */
  | { kind: 'timer'; nodeId: string; token: number; ms: number }
  | { kind: 'http'; nodeId: string; token: number };

export interface FlowRunState {
  status: FlowRunStatus;
  /** Node to run next while running; the node being waited on while waiting. */
  at: string | null;
  vars: FlowVars;
  wait: FlowWait | null;
  /** Source of wait tokens. Only ever grows. */
  seq: number;
  error?: string;
}

export type FlowInput =
  | { kind: 'start'; vars?: FlowVars }
  | { kind: 'reply'; token: number; text: string }
  /** A delay elapsed, or a question timed out. */
  | { kind: 'timer'; token: number }
  | {
      kind: 'http';
      token: number;
      /** HTTP status; 0 = no response (network error, timeout, blocked URL). */
      status: number;
      /** Parsed JSON body, or the text when it isn't JSON. */
      body?: unknown;
      error?: string;
    };

export interface HttpRequestSpec {
  method: HttpMethod;
  url: string;
  headers: Record<string, string>;
  body?: string;
}

/** Work the caller performs after advancing. The interpreter decides; the
 *  server (or the test runner) acts. */
export type FlowEffect =
  | { kind: 'send_message'; nodeId: string; text: string }
  | { kind: 'ask'; nodeId: string; text: string; options: string[] }
  /** Feed `{ kind: 'timer', token }` back after `delayMs`. */
  | { kind: 'schedule'; nodeId: string; token: number; delayMs: number }
  | { kind: 'tag'; nodeId: string; add: string[]; remove: string[] }
  /** Store `value` as the contact's attribute `key` (rendered already). */
  | { kind: 'set_attribute'; nodeId: string; key: string; value: string }
  | { kind: 'assign'; nodeId: string; to: AssignTarget }
  | { kind: 'close'; nodeId: string }
  /** `wait: false` — send and move on (webhook). `wait: true` — feed
   *  `{ kind: 'http', token, ... }` back with the response (rest). */
  | { kind: 'http'; nodeId: string; request: HttpRequestSpec; wait: false }
  | { kind: 'http'; nodeId: string; request: HttpRequestSpec; wait: true; token: number };

/** One line of a run's transcript (flow_run_steps). */
export interface FlowStep {
  nodeId: string;
  nodeType: FlowNodeType;
  /** Which way it went: 'next', 'else', 'rule:0', 'timeout', 'error', 'wait'… */
  outcome: string;
  detail?: Record<string, unknown>;
}

export interface FlowAdvance {
  state: FlowRunState;
  effects: FlowEffect[];
  steps: FlowStep[];
  /** False when the input didn't apply (stale token, run already finished);
   *  state is then unchanged and there is nothing to do. */
  applied: boolean;
}
